import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import PortalLayout from "@/layouts/PortalLayout";
import { api } from "@/lib/api";
import { Palette, Download, Sparkles, ArrowLeft, RotateCcw, Coffee, Calendar as CalendarIcon, Monitor, Star } from "lucide-react";
import EtOHStudioPublisher from "@/components/ContentStudioPublisher";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const VARIANTS = [
  { v: "fb", label: "Facebook / LinkedIn", w: 1200, h: 630 },
  { v: "post", label: "Post carré (1:1)", w: 1080, h: 1080 },
  { v: "feed", label: "Instagram + LinkedIn Feed", w: 1080, h: 1350 },
  { v: "story", label: "Instagram Story", w: 1080, h: 1920 },
  { v: "li_event", label: "LinkedIn Event Cover", w: 1920, h: 1080 },
  { v: "li", label: "LinkedIn post carré", w: 1200, h: 627 },
];

const TEMPLATES = [
  { id: "default", label: "Standard", icon: Star, c1: "263064", c2: "722F37", accent: "E33170", tag: "ÉVÉNEMENT" },
  { id: "coffee", label: "WineTech Coffee", icon: Coffee, c1: "7A1E5A", c2: "E33170", accent: "FFDCEB", tag: "WINETECH COFFEE · LIVE LINKEDIN" },
  { id: "monthly", label: "Monthly Meetup", icon: CalendarIcon, c1: "263064", c2: "722F37", accent: "E33170", tag: "RDV MENSUEL · LA WINETECH" },
  { id: "webinar", label: "Webinar", icon: Monitor, c1: "1E1B4B", c2: "E33170", accent: "6EE7B7", tag: "WEBINAIRE EN LIGNE" },
  { id: "tour", label: "WineTech Tour", icon: Star, c1: "263064", c2: "722F37", accent: "E33170", tag: "WINETECH TOUR" },
];

const PROMO_PRESETS = ["Save the date", "Inscription gratuite", "Places limitées", "Premium experience", "À ne pas manquer"];
const CTA_PRESETS = ["S'INSCRIRE → LAWINETECH.COM", "RÉSERVER MA PLACE", "REJOINDRE → LAWINETECH.COM", "JE M'INSCRIS", "EN LIGNE · LINKEDIN LIVE"];

export default function AdminEventVisuals() {
  const { id } = useParams();
  const [ctx, setCtx] = useState(null);
  const [template, setTemplate] = useState("default");
  const [variant, setVariant] = useState("feed");
  const [c1, setC1] = useState("263064");
  const [c2, setC2] = useState("722F37");
  const [accent, setAccent] = useState("E33170");
  const [tag, setTag] = useState("ÉVÉNEMENT");
  const [slogan, setSlogan] = useState("");
  const [promo, setPromo] = useState("");
  const [cta, setCta] = useState("");

  const applyTemplate = (id) => {
    const tpl = TEMPLATES.find((t) => t.id === id);
    if (!tpl) return;
    setTemplate(id);
    setC1(tpl.c1); setC2(tpl.c2); setAccent(tpl.accent); setTag(tpl.tag);
  };

  useEffect(() => {
    api.get(`/admin/events/${id}/visual-context`).then((r) => {
      setCtx(r.data);
      if (r.data.event?.description) setSlogan(r.data.event.description.slice(0, 120));
    }).catch(() => {});
  }, [id]);

  const previewUrl = useMemo(() => {
    const params = new URLSearchParams({ variant, template, c1, c2, accent });
    if (tag) params.set("tag", tag);
    if (slogan) params.set("slogan", slogan);
    if (promo) params.set("promo", promo);
    if (cta) params.set("cta", cta);
    const slug = ctx?.event?.slug || id;
    return `${BACKEND}/api/visuals/event/${slug}.png?${params.toString()}`;
  }, [ctx, id, variant, template, c1, c2, accent, tag, slogan, promo, cta]);

  const cw = VARIANTS.find((v) => v.v === variant)?.w || 1200;
  const ch = VARIANTS.find((v) => v.v === variant)?.h || 630;
  const ratio = cw / ch;

  const reset = () => {
    const tpl = TEMPLATES.find((t) => t.id === template);
    setC1(tpl.c1); setC2(tpl.c2); setAccent(tpl.accent); setTag(tpl.tag);
    setPromo(""); setCta(""); setSlogan(ctx?.event?.description?.slice(0, 120) || "");
  };

  if (!ctx) return <PortalLayout sidebar="admin"><div className="py-12 text-center text-neutral-500">Chargement…</div></PortalLayout>;
  const slug = ctx.event.slug || id;

  return (
    <PortalLayout sidebar="admin">
      <Link to={`/admin/events/${id}`} className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-wine-700" data-testid="back-to-event">
        <ArrowLeft className="h-3 w-3" />Retour à l&apos;événement
      </Link>
      <h1 className="mt-2 text-3xl font-bold text-navy md:text-4xl">
        <Sparkles className="mr-2 inline h-7 w-7 text-amber-500" />Visuels réseaux sociaux — {ctx.event.title}
      </h1>
      <p className="mt-1 text-neutral-600">Générez vos visuels social media pour cet événement (Coffee, Monthly, Webinar...).</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-[400px_1fr]">
        <div className="space-y-4">
          {/* Template */}
          <div className="rounded-2xl border border-neutral-100 bg-white p-4">
            <label className="text-xs font-bold uppercase text-neutral-600">Template</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {TEMPLATES.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    onClick={() => applyTemplate(t.id)}
                    className={`rounded-xl border-2 px-3 py-2 text-left text-xs transition-all ${template === t.id ? "border-wine-500 bg-wine-50" : "border-neutral-200 bg-white"}`}
                    data-testid={`tpl-${t.id}`}
                  >
                    <div className="flex items-center gap-1.5 font-bold text-neutral-900">
                      <Icon className="h-3.5 w-3.5" />{t.label}
                    </div>
                    <div className="mt-1 flex items-center gap-1">
                      <span className="inline-block h-3 w-3 rounded" style={{ background: `#${t.c1}` }} />
                      <span className="inline-block h-3 w-3 rounded" style={{ background: `#${t.c2}` }} />
                      <span className="inline-block h-3 w-3 rounded" style={{ background: `#${t.accent}` }} />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Format */}
          <div className="rounded-2xl border border-neutral-100 bg-white p-4">
            <label className="text-xs font-bold uppercase text-neutral-600">Format</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {VARIANTS.map((v) => (
                <button
                  key={v.v}
                  onClick={() => setVariant(v.v)}
                  className={`rounded-xl border-2 px-3 py-2 text-left text-xs transition-all ${variant === v.v ? "border-wine-500 bg-wine-50" : "border-neutral-200 bg-white"}`}
                  data-testid={`fmt-${v.v}`}
                >
                  <div className="font-bold text-neutral-900">{v.label}</div>
                  <div className="text-[10px] text-neutral-500">{v.w}×{v.h}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Colors */}
          <div className="rounded-2xl border border-neutral-100 bg-white p-4">
            <label className="text-xs font-bold uppercase text-neutral-600"><Palette className="mr-1 inline h-3 w-3" />Couleurs</label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              <div>
                <div className="text-[10px] uppercase text-neutral-500">Fond 1</div>
                <div className="flex items-center gap-1">
                  <input type="color" value={`#${c1}`} onChange={(e) => setC1(e.target.value.replace("#", ""))} className="h-8 w-12 rounded border" data-testid="color-c1" />
                  <input value={c1} onChange={(e) => setC1(e.target.value.replace("#", ""))} className="w-16 rounded border border-neutral-200 px-1.5 py-1 text-xs uppercase" />
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-neutral-500">Fond 2</div>
                <div className="flex items-center gap-1">
                  <input type="color" value={`#${c2}`} onChange={(e) => setC2(e.target.value.replace("#", ""))} className="h-8 w-12 rounded border" data-testid="color-c2" />
                  <input value={c2} onChange={(e) => setC2(e.target.value.replace("#", ""))} className="w-16 rounded border border-neutral-200 px-1.5 py-1 text-xs uppercase" />
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-neutral-500">Accent</div>
                <div className="flex items-center gap-1">
                  <input type="color" value={`#${accent}`} onChange={(e) => setAccent(e.target.value.replace("#", ""))} className="h-8 w-12 rounded border" data-testid="color-accent" />
                  <input value={accent} onChange={(e) => setAccent(e.target.value.replace("#", ""))} className="w-16 rounded border border-neutral-200 px-1.5 py-1 text-xs uppercase" />
                </div>
              </div>
            </div>
          </div>

          {/* Text */}
          <div className="space-y-3 rounded-2xl border border-neutral-100 bg-white p-4">
            <div>
              <label className="text-xs font-bold uppercase text-neutral-600">Bandeau (tag)</label>
              <input value={tag} onChange={(e) => setTag(e.target.value)} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="visual-tag" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-neutral-600">Slogan</label>
              <textarea value={slogan} onChange={(e) => setSlogan(e.target.value)} rows={2} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="visual-slogan" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-neutral-600">CTA</label>
              <input value={cta} onChange={(e) => setCta(e.target.value)} placeholder="S'INSCRIRE → LAWINETECH.COM" className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="visual-cta" />
              <div className="mt-1 flex flex-wrap gap-1">
                {CTA_PRESETS.map((p) => (
                  <button key={p} onClick={() => setCta(p)} className="rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-[10px] font-bold text-neutral-700 hover:bg-neutral-100" data-testid={`cta-${p}`}>{p}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-neutral-600">Sticker promo</label>
              <input value={promo} onChange={(e) => setPromo(e.target.value)} placeholder="Ex: Save the date" className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="visual-promo" />
              <div className="mt-1 flex flex-wrap gap-1">
                {PROMO_PRESETS.map((p) => (
                  <button key={p} onClick={() => setPromo(p)} className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-800 hover:bg-amber-100" data-testid={`promo-${p}`}>{p}</button>
                ))}
                <button onClick={() => setPromo("")} className="rounded-full border border-neutral-200 bg-white px-2 py-0.5 text-[10px] font-bold text-neutral-500">Aucun</button>
              </div>
            </div>
            <button onClick={reset} className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-wine-700" data-testid="visual-reset">
              <RotateCcw className="h-3 w-3" />Réinitialiser
            </button>
          </div>

          <a href={previewUrl} download={`lawinetech-${slug}-${template}-${variant}.png`} className="block w-full rounded-full bg-wine-600 px-5 py-3 text-center text-sm font-bold text-white hover:bg-wine-700" data-testid="visual-download">
            <Download className="mr-1 inline h-4 w-4" />Télécharger {cw}×{ch}
          </a>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-neutral-100 bg-neutral-50 p-4">
            <div className="text-[10px] uppercase text-neutral-500">Aperçu live</div>
            <div className="mt-2 overflow-hidden rounded-xl border border-neutral-200 bg-white">
              <img src={previewUrl} alt="Aperçu" className="block w-full" style={{ aspectRatio: ratio }} data-testid="visual-preview" />
            </div>
          </div>
          <EtOHStudioPublisher
            imageUrl={previewUrl}
            defaultText={ctx.event ? `${ctx.event.title}\n📅 ${(ctx.event.date || "").slice(0, 10)} · 📍 ${ctx.event.city || ctx.event.location || ""}\n\n${ctx.event.description || ""}\n\n👉 Inscriptions sur lawinetech.com` : ""}
            lockedEvent={ctx.event}
          />
        </div>
      </div>
    </PortalLayout>
  );
}
